import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SarabunNormal, SarabunBold, areFontsLoaded } from './fonts/sarabun-font';

// Thai font support
// NOTE: To properly support Thai characters, you need to:
// 1. Download a Thai font (e.g., Sarabun, Kanit) from Google Fonts
// 2. Convert the .ttf file using: https://peckconsulting.s3.amazonaws.com/fontconverter/fontconverter.html
// 3. Add the base64 font data to src/app/services/fonts/sarabun-font.ts
// See THAI_FONT_SETUP.md for detailed instructions

export interface CertificateData {
  id: string;
  userName: string;
  courseName: string;
  courseDescription: string;
  hoursStudied: string;
  studyDate: string;
  enrolledAt: string;
  completedAt: string | null;
  instructor: string;
  year: number;
  isVerified: boolean;
  certificateNumber?: string;
  certificationBySEC?: boolean; // หลักสูตรรับรองโดย ก.ล.ต. หรือไม่ (มีผลกับข้อความบนใบรับรอง)
  courseStartDate?: string | null; // วันที่เริ่มอบรมจริง (จาก course) — ใช้แสดงบนใบรับรอง
  courseEndDate?: string | null;   // วันสิ้นสุดอบรมจริง (จาก course)
}

@Injectable({
  providedIn: 'root'
})
export class UserCertificateService {
  private thsarabunFontAdded = false;

  constructor(private http: HttpClient) {}

  /**
   * Get certificate data from CPD hour record
   */
  getCertificateData(cpdHourId: string): Observable<any> {
    const apiUrl = `${environment.apiUrl}/cpd-hours/${cpdHourId}`;
    return this.http.get<any>(apiUrl);
  }

  /**
   * Generate and download certificate PDF
   */
  async generateCertificate(certificateData: CertificateData): Promise<void> {
    let holder: HTMLDivElement | null = null;
    try {
      console.log('🎓 Generating certificate for:', certificateData);

      // สร้างใบรับรองเป็น HTML แล้วแปลงเป็นรูปด้วย html2canvas
      // (ให้ browser จัดตำแหน่งวรรณยุกต์/สระภาษาไทยถูกต้อง แล้วค่อยฝังลง PDF)
      holder = document.createElement('div');
      holder.style.position = 'fixed';
      holder.style.left = '-99999px';
      holder.style.top = '0';
      holder.innerHTML = this.buildCertificateHtml(certificateData);
      document.body.appendChild(holder);

      // รอฟอนต์ Sarabun + รูปภาพ (โลโก้/ลายเซ็น) โหลดครบก่อน render
      await this.ensureFontsLoaded();
      await this.awaitImages(holder);

      const target = holder.firstElementChild as HTMLElement;
      const canvas = await html2canvas(target, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false
      });

      // A4 แนวนอน (landscape) เต็มหน้า
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 297, 210, undefined, 'FAST');

      const safeName = (certificateData.userName || 'certificate').replace(/\s+/g, '_');
      const fileName = `ใบรับรอง_${safeName}.pdf`;
      pdf.save(fileName);

      console.log('✅ Certificate generated successfully:', fileName);
    } catch (error) {
      console.error('❌ Error generating certificate:', error);
      throw error;
    } finally {
      if (holder && holder.parentNode) {
        holder.parentNode.removeChild(holder);
      }
    }
  }

  /**
   * สร้าง HTML ของใบรับรอง (A4 landscape 1123x794px) ใช้ฟอนต์ Sarabun
   */
  private buildCertificateHtml(data: CertificateData): string {
    const name = this.escapeHtml(data.userName || '-');
    const course = this.escapeHtml(data.courseName || '');
    // วันที่อบรมจริงจาก course (startDate/endDate) — ถ้าคนละวันให้แสดงเป็นช่วง, เอาแค่ วัน-เดือน-ปี
    const date = this.escapeHtml(
      this.formatTrainingDate(data.courseStartDate, data.courseEndDate, data.studyDate)
    );
    const hours = this.escapeHtml(this.formatHours(data.hoursStudied));
    // ข้อความบรรทัด "ได้เข้ารับการอบรม" เปลี่ยนตามการรับรองของ ก.ล.ต.
    const trainedLabel = data.certificationBySEC
      ? 'ได้เข้ารับการอบรมหลักสูตรการพัฒนาความรู้ต่อเนื่องผู้ประเมินหลัก(ก.ล.ต.)'
      : 'ได้เข้าร่วมการอบรมหลักสูตร';

    // กรอบใบรับรอง: หลักสูตรที่ ก.ล.ต.รองรับ = กรอบน้ำเงิน, ทั่วไป = กรอบส้ม
    const borderImg = data.certificationBySEC
      ? '/assets/images/background-1.png'
      : '/assets/images/background-2.png';

    return `
      <div style="width:1123px;height:794px;background:#ffffff;box-sizing:border-box;position:relative;
                  font-family:'Sarabun',sans-serif;color:#1e293b;">
        <img src="${borderImg}" alt="border"
             style="position:absolute;top:0;left:0;width:100%;height:100%;object-fit:fill;pointer-events:none;">
        <div style="position:relative;z-index:1;width:100%;height:100%;box-sizing:border-box;
                    display:flex;flex-direction:column;align-items:center;justify-content:flex-start;
                    padding:60px 96px;text-align:center;">
          <img src="/assets/images/logo.png" alt="VAT" style="height:112px;object-fit:contain;margin-bottom:6px;">
          <div style="font-size:34px;font-weight:700;line-height:1.2;">สมาคมผู้ประเมินค่าทรัพย์สินแห่งประเทศไทย</div>
          <div style="font-size:18px;font-weight:700;letter-spacing:1px;margin-bottom:16px;">THE VALUERS ASSOCIATION OF THAILAND</div>
          <div style="font-size:22px;margin-bottom:8px;">ออกหนังสือรับรองนี้ไว้เพื่อแสดงว่า</div>
          <div style="font-size:40px;font-weight:700;margin:4px 0 12px;">${name}</div>
          <div style="font-size:22px;line-height:1.4;">${trainedLabel}</div>
          <div style="font-size:28px;font-weight:700;margin:4px 0 14px;line-height:1.3;">&ldquo;${course}&rdquo;</div>
          <div style="font-size:22px;">วันที่ ${date}</div>
          <div style="font-size:22px;font-weight:700;margin-top:2px;">รวม ${hours} ชั่วโมง</div>
          <img src="/assets/images/sign-secretary.png" alt="signature" style="height:74px;object-fit:contain;margin-top:14px;">
          <div style="font-size:20px;font-weight:700;">( นายกิตติพงศ์  ผลประยูร )</div>
          <div style="font-size:18px;">เลขาธิการสมาคม</div>
        </div>
      </div>`;
  }

  /** รอฟอนต์ Sarabun (normal/bold) พร้อมใช้งานก่อน render */
  private async ensureFontsLoaded(): Promise<void> {
    try {
      const fonts: any = (document as any).fonts;
      if (fonts && fonts.load) {
        await Promise.all([
          fonts.load("400 22px 'Sarabun'"),
          fonts.load("700 40px 'Sarabun'")
        ]);
        await fonts.ready;
      }
    } catch { /* ถ้าเช็คฟอนต์ไม่ได้ ก็ปล่อยให้ render ต่อ */ }
  }

  /** รอให้ <img> ทุกตัวใน container โหลดเสร็จ (หรือ error) */
  private awaitImages(container: HTMLElement): Promise<void> {
    const imgs = Array.from(container.querySelectorAll('img'));
    return Promise.all(
      imgs.map(img =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>(res => { img.onload = () => res(); img.onerror = () => res(); })
      )
    ).then(() => undefined);
  }

  /** escape ข้อความก่อนใส่ลง HTML */
  private escapeHtml(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Add Thai font support to PDF
   * Uses embedded Sarabun font for Thai character support
   */
  private addThaiFont(pdf: jsPDF): void {
    // Only add font once per service instance
    if (this.thsarabunFontAdded) {
      return;
    }

    try {
      // Check if Thai fonts are loaded
      if (areFontsLoaded()) {
        // Add Sarabun font files to VFS
        pdf.addFileToVFS('Sarabun-normal.ttf', SarabunNormal);
        pdf.addFont('Sarabun-normal.ttf', 'Sarabun', 'normal');
        
        if (SarabunBold.length > 0) {
          pdf.addFileToVFS('Sarabun-bold.ttf', SarabunBold);
          pdf.addFont('Sarabun-bold.ttf', 'Sarabun', 'bold');
        }
        
        pdf.setFont('Sarabun');
        console.log('✅ Thai font (Sarabun) loaded successfully');
      } else {
        // Fallback to Helvetica if Thai fonts are not available
        pdf.setFont('helvetica');
        console.warn('⚠️ Thai fonts not loaded. Please follow instructions in THAI_FONT_SETUP.md');
        console.warn('⚠️ Thai characters may not display correctly');
      }
      
      this.thsarabunFontAdded = true;
    } catch (error) {
      console.error('❌ Error adding Thai font:', error);
      pdf.setFont('helvetica');
    }
  }

  /**
   * Draw decorative border for certificate
   */
  private drawCertificateBorder(pdf: jsPDF, width: number, height: number): void {
    // กรอบนอก (ส้มเข้ม)
    pdf.setLineWidth(2.5);
    pdf.setDrawColor(230, 126, 34);
    pdf.rect(8, 8, width - 16, height - 16);

    // กรอบใน (ส้มอ่อน)
    pdf.setLineWidth(0.8);
    pdf.setDrawColor(243, 156, 18);
    pdf.rect(13, 13, width - 26, height - 26);

    // ลายมุม
    pdf.setLineWidth(1.4);
    pdf.setDrawColor(230, 126, 34);
    const s = 16;
    const m = 13;
    // ซ้ายบน
    pdf.line(m, m, m + s, m); pdf.line(m, m, m, m + s);
    // ขวาบน
    pdf.line(width - m, m, width - m - s, m); pdf.line(width - m, m, width - m, m + s);
    // ซ้ายล่าง
    pdf.line(m, height - m, m + s, height - m); pdf.line(m, height - m, m, height - m - s);
    // ขวาล่าง
    pdf.line(width - m, height - m, width - m - s, height - m); pdf.line(width - m, height - m, width - m, height - m - s);
  }

  /** โหลดรูปภาพเป็น HTMLImageElement เพื่อฝังลง PDF */
  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }

  /** แปลงวันที่เป็นรูปแบบไทย เช่น "23 มีนาคม 2567" (พ.ศ.) */
  private formatThaiDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return String(dateString);
    const months = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear() + 543}`;
  }

  /** เทียบว่าเป็นวันเดียวกันหรือไม่ (ไม่สนใจเวลา) */
  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  /**
   * วันที่อบรมสำหรับใบรับรอง (เอาแค่ วัน-เดือน-ปี ไม่เอาเวลา)
   * - อบรมวันเดียว: "8 สิงหาคม 2569"
   * - อบรมหลายวัน: "8 สิงหาคม 2569 - 9 สิงหาคม 2569"
   * - ถ้าไม่มีวันที่ของ course ใช้ fallback (studyDate)
   */
  private formatTrainingDate(
    start: string | null | undefined,
    end: string | null | undefined,
    fallback: string | null | undefined
  ): string {
    const s = start ? new Date(start) : null;
    if (!s || isNaN(s.getTime())) {
      return this.formatThaiDate(fallback);
    }
    const startStr = this.formatThaiDate(start);
    const e = end ? new Date(end) : null;
    if (e && !isNaN(e.getTime()) && !this.isSameDay(s, e)) {
      return `${startStr} - ${this.formatThaiDate(end)}`;
    }
    return startStr;
  }

  /** จัดรูปแบบจำนวนชั่วโมง เช่น "6" หรือ "6.5" */
  private formatHours(value: string | number | null | undefined): string {
    const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
    if (isNaN(n)) return String(value ?? '-');
    return Number.isInteger(n) ? String(n) : String(n);
  }

  /**
   * Format date to readable string (คงไว้เผื่อการใช้งานเดิม)
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Generate certificate number based on CPD hour ID and date
   */
  generateCertificateNumber(cpdHourId: string, year: number): string {
    const prefix = 'VAT-CPD';
    const shortId = cpdHourId.substring(0, 8).toUpperCase();
    return `${prefix}-${year}-${shortId}`;
  }
}
